import type { Dispatcher } from "hooks";
import { ApiState } from "infrastructure/api";
import { RequestQueryMapperStrategy } from "consts/request-query";
import { defaultPaginateOptions, Paginate } from "consts/table";
import { Filters } from "consts/transactions";
import { RequestQueryMapper } from "util/request-query-mapper";
import type { Transaction } from "features/transactions/types";
import { Repository as BaseRepository, RepositoryActionType, SearchQueryParams } from "../../Repository";
import type {
    BankingSDKLoggingRepositoryState,
    BankingSDKLoggingRepository
} from "./types";

export class Repository extends BaseRepository<BankingSDKLoggingRepositoryState> implements BankingSDKLoggingRepository {
    public constructor(
        public readonly state: BankingSDKLoggingRepositoryState,
        protected readonly dispatch: Dispatcher<RepositoryActionType>,
        public readonly fetchHandler: (searchQueryParams?: string) => Promise<BankingSDKLoggingRepositoryState['data']>,
        private readonly transaction: Transaction
    ) {
        super(state, dispatch);
    }

    public get data() {
        return this.state.data;
    }

    public get rows() {
        return this.state.data?.data || [];
    }

    public get tablePaginator() {
        const {
            current_page: page,
            per_page: pageSize,
            total: rowCount
        } = {
            ...defaultPaginateOptions,
            ...this.state.data
        };

        return {
            pageSize,
            page: Math.max(0, page - 1),
            rowCount: Math.max(pageSize, rowCount)
        };
    }

    public override get isRepositoryEmpty() {
        return Boolean(
            [ApiState.Succeeded]
                .includes(this.state.apiState) &&
            !this.state.data?.data.length
        );
    }

    public override fetch(searchQueryParams: SearchQueryParams): Promise<void> {
        const {
            page,
            pageSize
        } = this.tablePaginator;

        return super.fetch(
            RequestQueryMapper.from('', {
                setStrategy: RequestQueryMapperStrategy.Set
            })
                .contains(Filters.transactionId, this.transaction.transactionId)
                .contains(Paginate.perPage, `${pageSize}`)
                .contains(Paginate.page, `${page}`)
                .concat(searchQueryParams.toString())
        );
    }

    public override init(searchQueryParams: RequestQueryMapper): Promise<void> {
        return super.init(
            searchQueryParams
                .contains(Paginate.perPage, `${this.tablePaginator.pageSize}`)
        );
    }
}
